import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { errMsg } from "@/lib/api";
import { enterGuestByUsername, startGuestSignup, verifyGuestSignup } from "@/lib/guest";

// "Continue as guest": username + email -> 6-digit code -> 7 days of Guest albums. Or sign back in with a username.
export const GuestLogin = ({ initial = "", onBack, signupOn }) => {
  const [returning, setReturning] = useState(!!initial || !signupOn);
  const [username, setUsername] = useState(initial);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [wait, setWait] = useState(0);
  const [website, setWebsite] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (wait <= 0) return undefined;
    const t = setTimeout(() => setWait(wait - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);
  const go = async (fn) => {
    setBusy(true);
    setError("");
    try { await fn(); } catch (err) {
      const closed = !signupOn && returning && err?.response?.status === 401;
      setError(closed ? "We couldn't find that guest username. New guest sign-up is closed right now, so ask the family for an invite." : errMsg(err));
    } finally { setBusy(false); }
  };
  const send = () => go(async () => { const r = await startGuestSignup(username, email, website); setSent(true); setWait(r.resend_after); });
  const submit = (e) => {
    e.preventDefault();
    if (returning) return go(async () => { await enterGuestByUsername(username); window.location.replace("/"); });
    if (!sent) return send();
    return go(async () => { await verifyGuestSignup(email, code); window.location.replace("/"); });
  };
  const title = returning ? (signupOn ? "Welcome back" : "Have a guest username from the family?") : sent ? "Check your email" : "Continue as guest";
  const sub = returning ? (signupOn ? "Enter your guest username. No password needed." : "Enter it here. No password needed.")
    : sent ? `We sent a 6-digit code to ${email}. It works for 10 minutes.`
      : "Pick a username and enter your email. We'll send a code, then you can view the family's guest albums for 7 days.";
  return (
    <form className="flex flex-col" onSubmit={submit} data-testid="guest-login-form">
      <h1 className="serif text-3xl text-neutral-900 text-center">{title}</h1>
      <p className="text-sm text-neutral-600 mt-2 mb-7 text-center" data-testid="guest-login-sub">{sub}</p>
      {!sent && (
        <input className="login-input" placeholder={returning ? "Guest username" : "Choose a username (3–20 letters, numbers, -)"} required autoComplete="username" autoCapitalize="none"
          minLength={returning ? 1 : 3} maxLength={returning ? 40 : 20} pattern={returning ? undefined : "[A-Za-z0-9-]{3,20}"}
          value={username} onChange={(e) => setUsername(e.target.value)} data-testid="guest-username-input" />
      )}
      {!returning && !sent && (
        <input className="login-input" type="email" placeholder="Your email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="guest-email-input" />
      )}
      {sent && (
        <input className="login-input text-center tracking-[0.5em] text-lg" inputMode="numeric" autoComplete="one-time-code" placeholder="••••••" maxLength={6} pattern="\d{6}" required autoFocus
          value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} data-testid="guest-code-input" />
      )}
      <input className="hp-field" tabIndex={-1} autoComplete="off" aria-hidden="true" value={website} onChange={(e) => setWebsite(e.target.value)} name="website" />
      {!signupOn && <p className="text-xs text-neutral-500 -mt-1 mb-3" data-testid="guest-signup-closed-note">New guest sign-up is currently closed. Ask the family for an invite.</p>}
      {error && <p className="text-sm text-red-600 mb-3" data-testid="guest-login-error">{error}</p>}
      <button className="btn-ink w-full" disabled={busy} data-testid="guest-login-submit">
        {busy ? <Loader2 className="animate-spin mx-auto" size={18} /> : returning ? "View photos" : sent ? "Start viewing" : "Email me a code"}
      </button>
      {sent && (
        <div className="flex justify-between mt-4 text-sm">
          <button type="button" className="auth-link" disabled={wait > 0 || busy} onClick={send} data-testid="guest-code-resend">{wait > 0 ? `Resend in ${wait}s` : "Resend code"}</button>
          <button type="button" className="auth-link" onClick={() => { setSent(false); setCode(""); }} data-testid="guest-code-change">Change email</button>
        </div>
      )}
      {signupOn && !sent && (
        <button type="button" className="auth-link mt-5 self-start" onClick={() => { setReturning(!returning); setError(""); }} data-testid="guest-mode-toggle">
          {returning ? "New here? Pick a username" : "Already have a guest username?"}
        </button>
      )}
      <button type="button" className="auth-link mt-3 self-start" onClick={onBack} data-testid="guest-login-back">Back to sign in</button>
    </form>
  );
};
